import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../../icons/Icon';

const routeIconName = (route) => {
  const p = String(route.path || '');
  if (p.startsWith('/dashboard')) return 'dashboard';
  if (p.startsWith('/products')) return 'products';
  if (p.startsWith('/sales')) return 'sales';
  if (p.startsWith('/stores')) return 'stores';
  if (p.startsWith('/inventory')) return 'inventory';
  if (p.startsWith('/customers')) return 'customers';
  if (p.startsWith('/suppliers')) return 'suppliers';
  if (p.startsWith('/purchases')) return 'purchases';
  if (p.startsWith('/kds')) return 'servicetickets';
  if (p.startsWith('/production')) return 'beaker';
  if (p.startsWith('/stock-transfers')) return 'stocktransfers';
  if (p.startsWith('/service-tickets')) return 'servicetickets';
  if (p.startsWith('/shift-management')) return 'attendance';
  if (p.startsWith('/attendance')) return 'attendance';
  if (p.startsWith('/staff-performance')) return 'staffperformance';
  if (p.startsWith('/marketing')) return 'marketing';
  if (p.startsWith('/staff')) return 'staff';
  if (p.startsWith('/roles')) return 'roles';
  if (p.startsWith('/settings')) return 'settings';
  if (p.startsWith('/cashier')) return 'reports';
  if (p.startsWith('/reports')) return 'reports';
  return 'list';
};

export default function SidebarItem({ route, active, collapsed, onClick, level = 0, expandedPath, onToggleExpanded }) {
  const navigate = useNavigate();
  const location = useLocation();
  const visibleChildren = (route.children || []).filter((child) => !child.hideFromMenu);
  // If there's only one visible child (e.g. Products -> All Products), don't show accordion nesting.
  const hasChildren = visibleChildren.length > 1;

  // Expand if current path is a child (or grandchild, etc.) of this route
  const isDescendantActive = (rt) => {
    if (location.pathname === rt.path) return true;
    if (rt.children) {
      return rt.children.some(child => isDescendantActive(child));
    }
    return false;
  };

  const isChildActive = isDescendantActive(route);

  // Accordion logic: expanded if the current expandedPath starts with our path
  const expanded = expandedPath && expandedPath.startsWith(route.path);

  const handleToggle = (e) => {
    if (hasChildren && !collapsed) {
      e.stopPropagation();
      if (onToggleExpanded) {
        onToggleExpanded(route.path);
      }
    } else {
      // For routes where nesting is suppressed, route.path is typically the correct target.
      navigate(route.path);
      if (onClick) onClick();
    }
  };

  const isSubItem = level > 0;
  const isActiveLeaf = active && !hasChildren;
  const isActiveParent = (active || isChildActive) && hasChildren;

  return (
    <div className="space-y-0.5">
      <button
        type="button"
        onClick={handleToggle}
        className={`group flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-all duration-200 ${
          isActiveLeaf
            ? 'bg-white text-[#0061B2] font-semibold shadow-sm'
            : isActiveParent
              ? 'bg-white/10 text-white font-semibold'
              : 'text-white/75 hover:bg-white/10 hover:text-white'
        } ${collapsed && !isSubItem ? 'justify-center px-0' : ''}`}
        title={collapsed ? route.label : undefined}
      >
        {!isSubItem && (
          <Icon
            name={routeIconName(route)}
            className={`h-[18px] w-[18px] shrink-0 ${
              isActiveLeaf
                ? 'text-[#0061B2]'
                : isActiveParent
                  ? 'text-white'
                  : 'text-white/70 group-hover:text-white'
            }`}
          />
        )}

        {isSubItem && route.icon && (
          <Icon
            name={route.icon.toLowerCase()}
            className={`h-4 w-4 shrink-0 ${isActiveLeaf ? 'text-[#0061B2]' : 'text-white/50 group-hover:text-white/80'}`}
          />
        )}

        {!collapsed && (
          <>
            <span className={`truncate flex-1 ${isSubItem ? 'text-xs' : 'text-sm'}`}>
              {route.label}
            </span>
            {hasChildren && (
              <Icon
                name={expanded ? 'chevrondown' : 'chevronright'}
                className={`h-4 w-4 transition-transform ${
                  isActiveParent ? 'text-white' : 'text-white/50 group-hover:text-white'
                }`}
              />
            )}
          </>
        )}
      </button>

      {hasChildren && expanded && !collapsed && (
        <div className="space-y-0.5 ml-5 border-l border-white/15 pl-2.5">
          {visibleChildren.map((child) => (
              <div key={child.path}>
                <SidebarItem
                  route={child}
                  active={location.pathname === child.path}
                  collapsed={collapsed}
                  onClick={onClick}
                  level={level + 1}
                  expandedPath={expandedPath}
                  onToggleExpanded={onToggleExpanded}
                />
              </div>
          ))}
        </div>
      )}
    </div>
  );
}
